import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { useFormPalette } from '../DynamicFormPrimitives';

/**
 * Horizontal flow step indicator for the FormFlowShell top bar.
 *
 * Just a row of left-aligned numbered chips — one per step — with the current
 * step highlighted and previously completed steps clickable (when allowBack is
 * true) to jump back. No rail, no progress fill, no markers: the chips carry
 * all the state on their own (filled circle = reached, muted = pending).
 */
export default function FormFlowStepper({
  steps = [],
  accent,
  allowBack = true,
  onBack,
}) {
  const palette = useFormPalette();
  const theme = useTheme();
  if (!Array.isArray(steps) || steps.length <= 1) return null;

  const accentColor = accent || theme.palette.primary.main;
  const currentIndex = Math.max(
    0,
    steps.findIndex((s) => s.status === 'CURRENT'),
  );

  const handleStepClick = (i, step) => {
    if (!allowBack || !onBack) return;
    if (i >= currentIndex) return;
    if (step.status !== 'COMPLETED') return;
    onBack(step.componentId);
  };

  return (
    <Box
      component="ol"
      role="group"
      aria-label="Flow progress"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        listStyle: 'none',
        m: 0,
        p: 0,
        gap: '6px',
        width: '100%',
        minWidth: 0,
      }}
    >
      {steps.map((step, i) => {
        const isCompleted = step.status === 'COMPLETED';
        const isCurrent = step.status === 'CURRENT';
        const clickable = allowBack && !!onBack && isCompleted;
        const label = step.stepperTitle || step.title || `Step ${i + 1}`;

        const handleKey = clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStepClick(i, step);
              }
            }
          : undefined;

        const reached = isCompleted || isCurrent;
        const numBg = reached ? accentColor : palette.chipBg;
        const numColor = reached
          ? theme.palette.getContrastText(accentColor)
          : palette.textSecondary;

        const rowBg = isCurrent ? alpha(accentColor, 0.08) : 'transparent';
        const titleColor = reached ? palette.textPrimary : palette.textSecondary;

        return (
          <Box
            key={step.componentId || i}
            component="li"
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : -1}
            aria-current={isCurrent ? 'step' : undefined}
            aria-disabled={!clickable && !isCurrent ? 'true' : undefined}
            aria-label={`${label}${isCompleted ? ' (completed)' : isCurrent ? ' (current step)' : ''}`}
            onClick={clickable ? () => handleStepClick(i, step) : undefined}
            onKeyDown={handleKey}
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '6px',
              px: '10px',
              py: '5px',
              borderRadius: '6px',
              cursor: clickable ? 'pointer' : 'default',
              backgroundColor: rowBg,
              transition: 'background-color 150ms ease',
              '&:hover': clickable
                ? {
                    backgroundColor: alpha(accentColor, isCurrent ? 0.12 : 0.06),
                  }
                : undefined,
              '&:focus-visible': clickable
                ? {
                    outline: 'none',
                    boxShadow: `0 0 0 2px ${alpha(accentColor, 0.4)}`,
                  }
                : undefined,
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                minWidth: 18,
                height: 18,
                px: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                backgroundColor: numBg,
                color: numColor,
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1,
                flexShrink: 0,
                transition: 'background-color 150ms ease, color 150ms ease',
              }}
            >
              {i + 1}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: isCurrent ? 600 : 500,
                lineHeight: 1.2,
                color: titleColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                flex: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
